import { useLocation } from '@reach/router';
import React, { useEffect, useState } from 'react';
import queryString from 'query-string';
import { formatNumber } from 'utils/formating';
import { useFetchPackages } from 'hooks/useFetchPackages';
import {
  IPriceInfo,
  IPromoCode,
  Package,
  PackagesList,
} from 'pages/newSubscription/models';
import { NewSubscriptionApi } from 'api/newSubscriptionApi';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectSelectedPackages,
  setPrice,
  setValidPromoCode,
  updateSelectedPackages,
} from 'reducers/misc';
import { Spinner } from 'components/spinner/Spinner';
import { AppPath, ERROR_MESSAGE } from 'appConstants';
import { SubscribeApi } from 'api/subscribeApi';
import ErrorMessage from 'components/errorMessage/ErrorMessage';
import { getVATAndPriceWithVAT } from 'helpers/priceCalculations';
import { PricingSummaryDetails } from 'components/pricingSummaryDetails/PricingSummaryDetails';

type ChoosePackageFormProps = {
  onSubmit: () => void;
  noMultiSubjectsAvailable?: boolean;
};

export const ChoosePackageForm: React.FC<ChoosePackageFormProps> = ({
  onSubmit,
  noMultiSubjectsAvailable,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();

  const pathname = location.pathname;
  const queryParams = location
    ? queryString.parse(location.search, { decode: false })
    : undefined;

  const selectedPackages = useSelector(selectSelectedPackages);
  const [allPackages, setAllPackages] = useState<PackagesList>([]);
  const [allPackagesViaSubject, setAllPackagesViaSubject] = useState<PackagesList>([]);
  const [updatePriceSelection, setUpdatePriceSelection] = useState<boolean>(false);
  const [allYears, setAllYears] = useState<string[]>([]);
  const [amountOffDiscount, setAmountOffDiscount] = useState<number | null>();
  const [promoCode, setPromoCode] = useState<IPromoCode>({
    value: '',
    error: '',
  });
  const [priceInfo, setPriceInfo] = useState<IPriceInfo>({
    price: 0,
    vat: 0,
    total: 0,
    discount: 0,
    appliedDiscountType: '',
  });

  const { packages, vatRate, amountOff, error } = useFetchPackages(
    location.pathname
  );

  useEffect(() => {
    if (!packages) return;
    setAmountOffDiscount(amountOff);
    setAllPackages(packages);
    setAllPackagesViaSubject(orderPackageBySubject(packages, selectedPackages));
    setAllYears(getYearList(packages));
  }, [packages]);

  useEffect(() => {
    if (!packages) return;
    setAllPackagesViaSubject(orderPackageBySubject(packages, selectedPackages));
  }, [packages, selectedPackages]);  

  useEffect(() => {
    if (updatePriceSelection) {
      setUpdatePriceSelection(false);
    }
  }, [updatePriceSelection]);  

  useEffect(() => {
    if (!packages) return;
    //preselect subscribed free trial packages
    const freeTrialPackages = packages
      .map((p: any) =>
        p.subjects.map((subject: any) =>
          subject.subscriptions.map((subscription: any) => {
            if (subscription.isFreeTrial) {
              return {
                ...subscription,
                ageGroup: p.name,
                subject: subject.name,
              };
            }
          })
        )
      )
      .flat()
      .flat()
      .filter((item: Package | undefined) => item);

    if (freeTrialPackages && freeTrialPackages.length > 0) {
      dispatch(updateSelectedPackages(freeTrialPackages));
    }

    //the subscribe url will include a param when the user has selected a package
    //from the pricing page and it gets redirected to the subscribe page
    let packageIds: any = queryParams ? queryParams.packageIds : null;

    if (!packageIds) return;
    packageIds = packageIds.indexOf(',') > -1 ? packageIds.split(",").map((item: any) => parseInt(item)) : packageIds;

      const paramPackage = Array.isArray(packageIds) ?
        packages.map((p: any) =>
          p.subjects.map((subject: any) =>
            subject.subscriptions.map((subscription: any) => {
              if (JSON.stringify(subscription.packageIDs) === JSON.stringify(packageIds)) {
                return {
                  ...subscription,
                  ageGroup: p.name,
                  subject: subject.name,
                };
              }
            })
          )
        )
        .flat()
        .flat()
        .filter((item: Package | undefined) => item)
      :
        packages.map((p: any) =>
          p.subjects.map((subject: any) =>
            subject.subscriptions.map((subscription: any) => {
              if (subscription.packageIDs == packageIds) {
                return {
                  ...subscription,
                  ageGroup: p.name,
                  subject: subject.name,
                };
              }
            })
          )
        )
        .flat()
        .flat()
        .filter((item: Package | undefined) => item)
      ;      

    if (paramPackage && paramPackage.length > 0) {
      dispatch(updateSelectedPackages(paramPackage));
    }
  }, [packages]);

  useEffect(() => {
    if (!vatRate) return;
    const [price, total, vat, discount, appliedDiscountType] =
      calculatePrice(vatRate);

    setPriceInfo({ price, vat, total, discount, appliedDiscountType });
  }, [selectedPackages]);





  const orderPackageBySubject = (packages: any, selectedPackages: any) => {
    let subjectList = packages[0].subjects.map((item: any) => { return { subject: item.name }; });
    let selectedClasses = allPackagesViaSubject.map((item: any) => item.selected );

    for(let i = 0; i < subjectList.length; i++){
      let classList = [];
      let subSelected = allPackagesViaSubject.length > 0 ? selectedClasses[i] : "Primary";

      for(let j = 0; j < packages.length; j++){
        let selectedSub = [];

        for(let k = 0; k < packages[j].subjects.length; k++){
          if(packages[j].subjects[k].name === subjectList[i].subject){
            selectedSub = packages[j].subjects[k].subscriptions;
          }
        }

        if(selectedPackages.length > 0){
          let matchedPackage = null;

          for(let l = 0; l < selectedSub.length; l++) {
            let idsMatched = false;           

            for(let m = 0; m < selectedSub[l].packageIDs.length; m++){
              if(
                selectedSub[l].packageIDs.length === selectedPackages[0].packageIDs.length && 
                selectedPackages[0].packageIDs.includes(selectedSub[l].packageIDs[m])
              ){
                matchedPackage = selectedPackages[0];
                idsMatched = true;
              } else {
                idsMatched = false;
              }
            }

            if(idsMatched){
              selectedSub[l].highlighted = true;
              subSelected = matchedPackage.ageGroup;
              break;
            }
          }
        }

        classList.push({ 
          name: packages[j].name,
          description: packages[j].description,
          subscriptions: selectedSub
        });
      }

      subjectList[i].classList = classList;
      subjectList[i].selected = subSelected;
    } 

    return subjectList;
  };





  const getYearList = (packages: Package[]) => {
    const yearList: string[] = [];

    packages.map((item: any, index: number) => {
      if(index === 0){
        item.subjects.map((subject: any, subject_index: number) => {
          if(subject_index === 0){
            subject.subscriptions.map((subscription: any) => {
              yearList.push(subscription.type);
            });
          }
        });
      }
    });

    return yearList;
  };

  const selectPackageBlock = (p: Package) => {
    let allPackagesViaSubjectCopy = allPackagesViaSubject;
    const subIndexes: number[] = findSelectedSubscriptionIndexes(allPackagesViaSubjectCopy, p);

    let selectedSubs = allPackagesViaSubjectCopy[subIndexes[0]].classList[subIndexes[1]].subscriptions;
    let selectedSub = allPackagesViaSubjectCopy[subIndexes[0]].classList[subIndexes[1]].subscriptions[subIndexes[2]];
    let nonMultiSubjectLesson: boolean = selectedSub.packageIDs.length <= 1;
    let multiSubjectLesson: any = null;
    let multiSubjectLessonSelected: any = false;
    let allSelectedPackageIds: number[] = [];
    let multiPackagesToSelect: any = [];
    let multiPackagesToUnselect: any = [];
    let multiLessonAgeGroup = "";

    selectedSubs.map((item: any, index: number) => {
      if(index !== subIndexes[2]){
        item.highlighted = false;
      }

      return item;
    });

    selectedSub.highlighted = selectedSub.highlighted ? !selectedSub.highlighted : true;

    /* Select multi package when both lessons are selected - START */
    if(!noMultiSubjectsAvailable){
      allPackagesViaSubjectCopy.map((item: any) => {
        item.classList.map((class_item: any) => {
          class_item.subscriptions.map((sub_item: any) => {
            if(sub_item.highlighted && sub_item.packageIDs.length === 1){
              sub_item.packageIDs.map((package_id_item: number) => {
                allSelectedPackageIds.push(package_id_item);
                return package_id_item;
              });
            } else {
              multiSubjectLesson = item;
            }
  
            return sub_item;
          });
        });
  
        return item;
      });
  
      if(allSelectedPackageIds.length > 0 && multiSubjectLesson){
        let multiSubClassList = multiSubjectLesson.classList;
        
        for(let i = 0; i < multiSubClassList.length; i++) {
          let multiSubSubList = multiSubClassList[i].subscriptions;
  
          for(let j = 0; j < multiSubSubList.length; j++){
            let packageIDsList = multiSubSubList[j].packageIDs;
            let idsMatched = false;
  
            for(let k = 0; k < packageIDsList.length; k++){
              if(allSelectedPackageIds.includes(packageIDsList[k])){
                idsMatched = true;
              } else {
                idsMatched = false;
                break;
              }   
            }
  
            if(idsMatched){
              multiSubSubList[j].subject = multiSubjectLesson.subject;
              multiLessonAgeGroup = multiSubClassList[i].name;
              multiPackagesToSelect.push(multiSubSubList[j]);
            }           
          }
        }
  
        multiSubjectLesson.classList.map((class_item: any) => {
          class_item.subscriptions.map((sub_item: any) => {
            if(sub_item.highlighted){
              sub_item.packageIDs.map((pi_item: any) => {
                p.packageIDs.map((p_pi_item: any) => {
                  if(pi_item === p_pi_item){
                    multiSubjectLessonSelected = sub_item;
                  }
                  return p_pi_item;
                });
                return pi_item;
              });
            }
            return sub_item;
          });
          return class_item;
        });
      }
    }
    /* Select multi package when both lessons are selected - END */

    //handle multi subject auto selection
    if(multiPackagesToSelect.length > 0 || multiSubjectLessonSelected){
      allPackagesViaSubjectCopy.map((item: any) => {
        item.classList.map((class_item: any) => {
          class_item.subscriptions.map((sub_item: any) => {
            if(sub_item.highlighted && sub_item.packageIDs.length === 1){
              sub_item.highlighted = false;
            }
  
            return sub_item;
          });
        });
  
        return item;
      });
    } else if (nonMultiSubjectLesson) {
      allPackagesViaSubjectCopy.map((item: any) => {
        item.classList.map((class_item: any) => {
          class_item.subscriptions.map((sub_item: any) => {
            if(sub_item.highlighted && sub_item.packageIDs.length > 1){
              sub_item.highlighted = false;
              multiPackagesToUnselect.push(sub_item);
            }
  
            return sub_item;
          });
        });
  
        return item;
      });
    }

    if(multiPackagesToSelect.length > 0){
      const selectedPackageIndexes: number[] = findSelectedSubscriptionIndexes(allPackagesViaSubjectCopy, selectedPackages[0]);

      allPackagesViaSubjectCopy[selectedPackageIndexes[0]].classList[selectedPackageIndexes[1]].subscriptions[selectedPackageIndexes[2]].highlighted = false;

      multiPackagesToSelect[0].ageGroup = multiLessonAgeGroup;
      selectPackage(multiPackagesToSelect[0], true);
    } else {
      if(multiSubjectLessonSelected){
        multiSubjectLessonSelected.highlighted = false;

        selectPackage(p, true);
      } else {
        selectPackage(p, nonMultiSubjectLesson && multiPackagesToUnselect.length > 0);
      }
    }  
    
    setAllPackagesViaSubject(allPackagesViaSubjectCopy);
    setUpdatePriceSelection(true);      
  };

  const findSelectedSubscriptionIndexes = (allPackagesViaSubjectCopy: any, p: Package) => {
    for(let i = 0; i < allPackagesViaSubjectCopy.length; i++){
      if(allPackagesViaSubjectCopy[i].subject === p.subject){
        let classList = allPackagesViaSubjectCopy[i].classList;

        for(let j = 0; j < classList.length; j++){
          if(classList[j].name === p.ageGroup){
            for(let k = 0; k < classList[j].subscriptions.length; k++){
              let indexArr = null;

              for(let l = 0; l < classList[j].subscriptions[k].packageIDs.length; l++){
                if(
                  classList[j].subscriptions[k].packageIDs.length === p.packageIDs.length && 
                  p.packageIDs.includes(classList[j].subscriptions[k].packageIDs[l])
                ){
                  indexArr = [i, j, k, l];
                } else {
                  indexArr = null;
                }
              }
  
              if(indexArr && indexArr.length > 0){
                return indexArr;
              }
            }
          }
        }
      }
    }

    return [0, 0, 0, 0];
  };

  const updateClassChange = (index: number, class_item_name: string) => {
    let allPackagesViaSubjectCopy = allPackagesViaSubject;
    let highlightedPackage = null;

    allPackagesViaSubjectCopy[index].selected = class_item_name;

    allPackagesViaSubjectCopy[index].classList.map((item: any) => {
      return item.subscriptions.map((sub_item: any) => {
        if(sub_item.highlighted){
          sub_item.highlighted = false;
          sub_item.subject = allPackagesViaSubjectCopy[index].subject;
          highlightedPackage = sub_item;
        }
        
        return sub_item;
      });
    });

    if(highlightedPackage){
      selectPackage(highlightedPackage, false);
    }

    setAllPackagesViaSubject(allPackagesViaSubjectCopy);
    setUpdatePriceSelection(true);
  };

  const selectPackage = (p: Package, clear_package_list: boolean) => {
    const packageList = selectedPackages;
    let includesPackage: any[] = [];
    let filteredPackages: any[] = [];

    if(clear_package_list){
      dispatch(updateSelectedPackages([p]));
    } else {
      for(let i = 0; i < packageList.length; i++){
        let foundPackage = false;
  
        for(let j = 0; j < packageList[i].packageIDs.length; j++){
          if(
            packageList[i].packageIDs.length === p.packageIDs.length && 
            p.packageIDs.includes(packageList[i].packageIDs[j])
          ){
            foundPackage = true;
          } else {
            foundPackage = false;
          }
  
          if(foundPackage){
            includesPackage.push(packageList[i]);
          } else {
            filteredPackages.push(packageList[i]);
            break;
          }
        }
      }
  
      if (includesPackage.length > 0) {
        dispatch(updateSelectedPackages(filteredPackages));
      } else {
        /*Clear packages of the same subject and different age group of the selected package*/
        const filteredPackageList = packageList.filter(
          (i: Package) => i.subject !== p.subject
        );
        dispatch(updateSelectedPackages([...filteredPackageList, p]));
      }
    }
  };

  const calculatePrice = (vatRate: number) => {
    //total without vat
    const price = selectedPackages.reduce(function (prev, cur) {
      return prev + cur.price;
    }, 0);

    const VATValue = price * (vatRate / 100);

    const priceWithVAT = price + VATValue;

    //----- Multi package discount -----///
    const multiPackageDiscountedTotalPriceWithoutVat = selectedPackages.reduce(
      function (prev, cur) {
        return prev + cur.price - cur.multiPurchaseDiscount;
      },
      0
    );
    const multiPackageDiscountedTotalPriceWithVat = selectedPackages.reduce(
      function (prev, cur) {
        return (
          prev +
          getVATAndPriceWithVAT(cur.price - cur.multiPurchaseDiscount, vatRate)
            .priceWithVAT
        );
      },
      0
    );

    const multiPackageVATValue = selectedPackages.reduce(function (prev, cur) {
      return prev + (cur.price - cur.multiPurchaseDiscount) * (vatRate / 100);
    }, 0);

    const multiPackageDiscount =
      price - multiPackageDiscountedTotalPriceWithoutVat;
    //------------------------------------//

    //------- Percentage of discount -------- //

    const percentageOffDiscountedTotalPriceWithoutVat = selectedPackages.reduce(
      function (prev, cur) {
        return prev + cur.price - cur.promoCodeDiscount;
      },
      0
    );
    const percentageOffDiscountedTotalPriceWithVat = selectedPackages.reduce(
      function (prev, cur) {
        return (
          prev +
          getVATAndPriceWithVAT(cur.price - cur.promoCodeDiscount, vatRate)
            .priceWithVAT
        );
      },
      0
    );

    const percentageOffVATValue = selectedPackages.reduce(function (prev, cur) {
      return prev + (cur.price - cur.promoCodeDiscount) * (vatRate / 100);
    }, 0);

    const percentageOffDiscount =
      price - percentageOffDiscountedTotalPriceWithoutVat;

    //------------------------------------//

    //------- Amount of discount -------- //
    const accumulatedDiscounts = selectedPackages.reduce(function (prev, cur) {
      return prev + cur.promoCodeDiscount;
    }, 0);

    let amountOffDiscountedTotalPriceWithoutVat;
    if (amountOffDiscount && accumulatedDiscounts > amountOffDiscount) {
      amountOffDiscountedTotalPriceWithoutVat = price - amountOffDiscount;
    } else {
      amountOffDiscountedTotalPriceWithoutVat = selectedPackages.reduce(
        function (prev, cur) {
          return prev + (cur.price - cur.promoCodeDiscount);
        },
        0
      );
    }

    const amountOffDiscountedTotalPriceWithVAT =
      amountOffDiscountedTotalPriceWithoutVat * 1.2;

    const amountOffDiscountValue = amountOffDiscount;

    const amountOffVATValue =
      amountOffDiscountedTotalPriceWithVAT -
      amountOffDiscountedTotalPriceWithoutVat;

    //------------------------------------//

    //------- Select the right discount-------- //

    const getSelectedPromoCodeDiscount = () => {
      if (amountOffDiscount) {
        return [
          amountOffDiscountedTotalPriceWithVAT,
          amountOffVATValue,
          amountOffDiscountValue,
        ];
      } else {
        return [
          percentageOffDiscountedTotalPriceWithVat,
          percentageOffVATValue,
          percentageOffDiscount,
        ];
      }
    };

    const [
      discountedTotalPriceWithVAT,
      discountedVATValue,
      discountValue,
    ]: any = getSelectedPromoCodeDiscount();

    //------- Final Discount Value -------- //
    const getDiscountedPrice = () => {
      if (selectedPackages.length > 1) {
        if (
          multiPackageDiscountedTotalPriceWithVat <= discountedTotalPriceWithVAT
        ) {
          return [
            multiPackageDiscountedTotalPriceWithVat,
            multiPackageVATValue,
            multiPackageDiscount,
          ];
        }
        if (
          multiPackageDiscountedTotalPriceWithVat > discountedTotalPriceWithVAT
        ) {
          return [
            discountedTotalPriceWithVAT,
            discountedVATValue,
            discountValue,
          ];
        }
      } else {
        return [discountedTotalPriceWithVAT, discountedVATValue, discountValue];
      }
    };

    const [
      finalDiscountTotalPriceWithVAT,
      finalDiscountedVATValue,
      finalDiscountValue,
    ]: any = getDiscountedPrice();

    //------------------------------------//

    //------- Final Price -------- //
    const finalPrice =
      finalDiscountTotalPriceWithVAT &&
      finalDiscountTotalPriceWithVAT < priceWithVAT
        ? finalDiscountTotalPriceWithVAT
        : priceWithVAT;

    const finalVATValue =
      finalDiscountTotalPriceWithVAT &&
      finalDiscountTotalPriceWithVAT < priceWithVAT
        ? finalDiscountedVATValue
        : VATValue;

    const finalDiscount =
      finalDiscountTotalPriceWithVAT &&
      finalDiscountTotalPriceWithVAT < priceWithVAT
        ? finalDiscountValue
        : 0;

    const finalDiscountType: string =
      selectedPackages.length > 1
        ? multiPackageDiscountedTotalPriceWithVat < discountedTotalPriceWithVAT
          ? 'Multipack'
          : 'Promo code'
        : 'Promo code';

    //------------------------------------//

    return [
      formatNumber(price),
      formatNumber(finalPrice),
      formatNumber(finalVATValue),
      formatNumber(finalDiscount),
      finalDiscountType,
    ];
  };

  const updateSelectedPackagesWithDiscount = (packages: any) => {
    const allUpdatedPackages = packages
      .map((p: any) =>
        p.subjects.map((i: any) =>
          i.subscriptions.map((j: any) => ({
            ...j,
            ageGroup: p.name,
            subject: i.name,
          }))
        )
      )
      .flat()
      .flat()
      .flat();

    let updatedSelectedPackages: any[] = [];

    for(let i = 0; i < selectedPackages.length; i++){
      let foundPackage = false;

      for(let j = 0; j < allUpdatedPackages.length; j++){
        for(let k = 0; k < allUpdatedPackages[j].packageIDs.length; k++){
          if(
            allUpdatedPackages[j].packageIDs.length === selectedPackages[i].packageIDs.length && 
            selectedPackages[i].packageIDs.includes(allUpdatedPackages[j].packageIDs[k])
          ){
            foundPackage = true;
          } else {
            foundPackage = false;
            break;
          }
  
          if(foundPackage){
            updatedSelectedPackages.push(allUpdatedPackages[j]);
          }
        }
      }
    }	

    updatedSelectedPackages.length > 0 &&
      dispatch(updateSelectedPackages(updatedSelectedPackages));
  };

  const applyPromoCode = async (vatRate: number) => {
    let result;
    if (AppPath.SUBSCRIBE === pathname) {
      result = await SubscribeApi.getSubscriptionsWithPromocode(
        promoCode.value
      );
    } else {
      result = await NewSubscriptionApi.checkPromoCode(promoCode.value);
    }

    if (result.data) {
      setPromoCode({
        value: '',
        error: '',
      });
      setAmountOffDiscount(result.data.amountOff);
      setAllPackages(result.data.generalPackages);
      setAllPackagesViaSubject(orderPackageBySubject(result.data.generalPackages, selectedPackages));
      setAllYears(getYearList(result.data.generalPackages));
      updateSelectedPackagesWithDiscount(result.data.generalPackages);
      dispatch(setValidPromoCode(promoCode.value));
    }

    if (result.error) {
      setPromoCode({ value: '', error: result.error.message });
    }
  };

  const onContinue = () => {
    if (!vatRate) return;

    dispatch(
      setPrice({
        price: priceInfo.price,
        vat: priceInfo.vat,
        total: priceInfo.total,
        vatRate,
        discount: priceInfo.discount,
        discountType: priceInfo.appliedDiscountType,
      })
    );
    onSubmit();
  };

  if (error) {
    return <ErrorMessage message={ERROR_MESSAGE}></ErrorMessage>;
  }

  if (allPackages.length === 0 && !error) {
    return (
      <div
        style={{
          minHeight: ' calc(100vh - 300px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Spinner />
      </div>
    );
  }

  return (
    <div className="PricingColRow">
      <div className="PCRowInner SmGap">
        <div className="LeftCol">
          <div className="CPFormContainer">
            <div className="HdrRow">
              <div className="FillerCell"></div>
              {allYears && allYears.map((year_str: string, index: number) => {
                return (
                  <React.Fragment key={index}>
                    {year_str.indexOf("5") > -1 ?
                      <div className="YearCell Highlight"><span className="BestValTxt">Best Value</span>{year_str}</div>
                      :
                      <>
                        <div className="YearCell">{year_str}</div>
                        <div className="CellSpacer"></div>                      
                      </>             
                    }
                  </React.Fragment>
                );
              })}                       
            </div>
            {allPackagesViaSubject && allPackagesViaSubject.map((item: any, index: number) => {
              const selectedSubscription = item.classList.filter((class_item: any) => class_item.name === item.selected)[0].subscriptions;
              const radioName = item.subject.split(" ").join("_");

              return (
                <div className="SubjectRow" key={"Subject_Row_" + index}>
                  <div className="SubDetailsCell">
                    <div className="SubjectName">{item.subject}</div>
                    <div className="ClassOptList">
                      {item.classList.map((class_item: any, class_index: number) => {
                        return (
                          <div className="CLOpt" key={"Class_Opt_" + class_index}>
                            <div className="Checkbox">
                              <input 
                                id={radioName + "_" + class_item.name + "_" + class_index} 
                                type="radio" 
                                name={radioName + "_Class_List"} 
                                checked={class_item.name === item.selected}
                              />
                              <label 
                                className="CustomInput" 
                                htmlFor="PE_EYFS"
                                onClick={() => updateClassChange(index, class_item.name)} >
                                  {class_item.name}
                                </label>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  {selectedSubscription && selectedSubscription.map((sub_item: any, sub_index: number) => {
                    let pricePerYearTxt = !sub_item.pricePerYear ? [] : sub_item.pricePerYear.split(";");

                    return (
                      <>
                        <div 
                          className={
                            "PriceCell" + 
                            (sub_item.type.indexOf("5") > -1 ? " Highlight" : "") + 
                            (sub_item.isAvailableForSchool === false ? " DisablePC" : "")
                          }
                        >
                          {sub_item.type.indexOf("5") > -1 && <div className="SmBestValLabel">Best Value</div>}
                          <div className="SmYearBlock">{allYears[sub_index]}</div>
                          <div 
                            className={"PriceContainer" + (sub_item.highlighted ? " Highlight" : "")}
                            onClick={() => selectPackageBlock({...sub_item, subject: item.subject, ageGroup: item.selected })}
                          >
                            <div className="PCInner">
                              <div className="PriceRadioBtn"></div>
                              <div className="PriceTxt">£{sub_item.price.toFixed(2)}</div>
                              {pricePerYearTxt && pricePerYearTxt.length > 0 && pricePerYearTxt.map((price_text_item: string, price_text_index: number) => {
                                return (
                                  <div className="PriceDesc" key={"Price_Text_" + price_text_index}>{price_text_item}</div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                        {(selectedSubscription.length - 1) !== sub_index && <div className="CellSpacer"></div>}
                      </>
                    );
                  })}
                </div>
              );
            })}                  
          </div>
          {allPackages &&
            <p className="PackageDesc">
              {allPackages.map((item: any, index: number) => {
                return (<span key={index}>{item.name}: {item.description}</span>);
              })}
            </p>
          }
        </div>
        <div className="RightCol">
          <PricingSummaryDetails 
            addPromoStep={true}
            promoCode={promoCode}
            setPromoCode={setPromoCode}
            vatRate={vatRate}
            applyPromoCode={applyPromoCode}
            onContinue={onContinue}
            btnText="Continue"
          />
        </div>
      </div>
    </div>
  );
};
